import { useState } from 'react';
import type { FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeaderNew } from '@/components/ui/PageHeaderNew';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';
import { EstimatesTopBar } from './components/EstimatesTopBar';
import { EstimatesTable } from './components/EstimatesTable';
import { useEstimateDetail } from '../shared/hooks/useEstimateDetail';
import { useEstimateItems } from '../shared/hooks/useEstimateItems';
import { useConvertEstimateToBoq } from '../shared/hooks/useConvertEstimateToBoq';
import { useEstimateVersions } from '../shared/hooks/useEstimateVersions';
import { useUpdateEstimateStatus } from '../shared/hooks/useUpdateEstimateStatus';
import { useEstimatePricing } from '../shared/hooks/useEstimatePricing';
import { useEstimateResources } from '../shared/hooks/useEstimateResources';
import { useEstimateBoqLinks } from '../shared/hooks/useEstimateBoqLinks';
import { useEstimateNorms, useApplyNormToItem } from '../shared/hooks/useEstimateNormsApply';
import { useApplyRatesToEstimate, useOverrideResourcePrice } from '../shared/hooks/useEstimatePricingApply';
import { useProjectZones } from '../../projects/zones/hooks/useZones';
import { useProjectPhases } from '../../projects/phases/hooks/usePhases';
import { EstimateImportModal } from './components/EstimateImportModal';
import { ProcessApplyDrawer } from './components/ProcessApplyDrawer';
import type { EstimateBoqLink, EstimateHeader, EstimateItem, EstimateItemInput, EstimateItemResource, EstimateStatus, EstimateVersion } from '../shared/types/estimate.types';

const statusMeta: Record<EstimateStatus, { label: string; variant: BadgeProps['variant'] }> = {
  draft: { label: 'Nháp', variant: 'neutral' },
  reviewing: { label: 'Đang duyệt', variant: 'warning' },
  approved: { label: 'Đã duyệt', variant: 'success' },
  locked: { label: 'Đã khóa', variant: 'info' },
  converted_to_boq: { label: 'Đã chuyển BOQ', variant: 'purple' },
  revised: { label: 'Điều chỉnh', variant: 'blue' },
  cancelled: { label: 'Đã hủy', variant: 'danger' },
};

type EstimateTab = 'overview' | 'items' | 'resources' | 'norms' | 'pricing' | 'zones' | 'versions' | 'boq-links' | 'logs';

const detailTabs: Array<{ id: EstimateTab; label: string; icon: string }> = [
  { id: 'overview', label: 'Tổng quan', icon: 'ri-dashboard-line' },
  { id: 'items', label: 'Hạng mục', icon: 'ri-list-check-3' },
  { id: 'resources', label: 'Tài nguyên', icon: 'ri-stack-line' },
  { id: 'norms', label: 'Định mức', icon: 'ri-book-open-line' },
  { id: 'pricing', label: 'Đơn giá', icon: 'ri-price-tag-3-line' },
  { id: 'zones', label: 'Theo khu vực', icon: 'ri-map-pin-line' },
  { id: 'versions', label: 'Phiên bản', icon: 'ri-history-line' },
  { id: 'boq-links', label: 'BOQ liên kết', icon: 'ri-node-tree' },
  { id: 'logs', label: 'Nhật ký', icon: 'ri-file-list-3-line' },
];

function formatCurrency(value: number) {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(value || 0);
}

export function EstimateDetailPage() {
  const { id: estimateId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [editingItem, setEditingItem] = useState<EstimateItem | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [processItem, setProcessItem] = useState<EstimateItem | null>(null);
  const [deleteItemTarget, setDeleteItemTarget] = useState<EstimateItem | null>(null);
  const [activeTab, setActiveTab] = useState<EstimateTab>('items');
  const { estimate, isLoading: isLoadingEstimate, error: estimateError } = useEstimateDetail(estimateId);
  const { items, stats, isLoading: isLoadingItems, error: itemsError, createItem, updateItem, deleteItem, isCreating, isUpdating } = useEstimateItems(estimateId);
  const { convertToBoq, isConverting } = useConvertEstimateToBoq(estimateId);
  const { versions, createSnapshot, isCreatingSnapshot } = useEstimateVersions(estimateId);
  const { resources, isLoading: isLoadingResources } = useEstimateResources(estimateId);
  const { links, isLoading: isLoadingLinks } = useEstimateBoqLinks(estimateId);
  const { updateStatus, isUpdatingStatus } = useUpdateEstimateStatus(estimateId);

  const [overheadPercent, setOverheadPercent] = useState(0);
  const [profitPercent, setProfitPercent] = useState(0);
  const [taxPercent, setTaxPercent] = useState(10);

  const pricing = useEstimatePricing(items, overheadPercent, profitPercent, taxPercent);

  const selectedItem = items.find((item) => item.id === selectedItemId) || null;
  const status = (estimate?.status_code || 'draft') as EstimateStatus;
  const meta = statusMeta[status] || statusMeta.draft;
  const error = estimateError || itemsError;
  const canConvert = estimate && ['approved', 'locked'].includes(String(estimate.status_code));
  const canEdit = estimate && !['locked', 'converted_to_boq', 'cancelled'].includes(String(estimate.status_code));
  const canSendReview = estimate && String(estimate.status_code) === 'draft' && pricing.isValidForReview;
  const canLock = estimate && ['approved'].includes(String(estimate.status_code));

  const handleConvertToBoq = async () => {
    try {
      const result = await convertToBoq();
      navigate(`/construction/boq/${result.boqId}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Không chuyển được dự toán sang BOQ.');
    }
  };

  const handleCreateItem = async (input: Omit<EstimateItemInput, 'company_id' | 'project_id' | 'estimate_id'>) => {
    if (!estimate) return;
    await createItem({
      ...input,
      company_id: estimate.company_id,
      project_id: estimate.project_id || null,
      estimate_id: estimate.id,
      sort_order: items.length + 1,
    });
    setShowAddItem(false);
  };

  const handleUpdateItem = async (input: Omit<EstimateItemInput, 'company_id' | 'project_id' | 'estimate_id'>) => {
    if (!editingItem) return;
    await updateItem({ id: editingItem.id, updates: input });
    setEditingItem(null);
  };

  const handleDeleteItem = async (item: EstimateItem) => {
    setDeleteItemTarget(item);
  };

  const confirmDeleteItem = async () => {
    if (!deleteItemTarget) return;
    await deleteItem(deleteItemTarget.id);
    setDeleteItemTarget(null);
  };

  const handleSendReview = async () => {
    await updateStatus('reviewing');
  };

  const handleApprove = async () => {
    await updateStatus('approved');
  };

  const handleLock = async () => {
    if (!estimate) return;
    await createSnapshot({ estimate, items, createdBy: user?.id || null, note: 'Khóa phiên bản dự toán' });
    await updateStatus('locked');
  };

  const handleExportCsv = () => {
    if (!estimate) return;
    const header = ['Ma', 'Ten hang muc', 'Don vi', 'Khoi luong', 'Don gia', 'Vat tu', 'Nhan cong', 'Thiet bi', 'Thau phu', 'Thanh tien'];
    const rows = items.map((item) => [
      item.item_code,
      item.item_name,
      item.unit,
      item.quantity,
      item.unit_price,
      item.material_cost,
      item.labor_cost,
      item.equipment_cost,
      item.subcontract_cost,
      item.total_amount,
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${estimate.estimate_code || 'estimate'}-items.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="relative flex h-full flex-col bg-slate-50">
      <div className="flex-shrink-0 p-4">
        <PageHeaderNew
          title={isLoadingEstimate ? 'Chi tiết dự toán' : estimate?.estimate_name || 'Không tìm thấy dự toán'}
          subtitle={
            estimate
              ? `${estimate.estimate_code} | KH: ${estimate.customer_name || estimate.project?.name || 'Chưa gắn khách hàng'}`
              : 'Dữ liệu dự toán sẽ hiển thị sau khi tải xong'
          }
          onBack={() => window.history.back()}
        />

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge variant={meta.variant}>{meta.label}</Badge>
          {estimate?.source === 'construction_boqs' && <Badge variant="outline">Nguồn fallback BOQ</Badge>}
          {estimate?.version_no && <Badge variant="secondary">Version {estimate.version_no}</Badge>}
          {pricing.warningCount > 0 && <Badge variant="danger">{pricing.warningCount} lỗi dữ liệu</Badge>}
        </div>

        <div className="mt-4">
          <EstimatesTopBar stats={stats} />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden px-4 pb-4">
        {error ? (
          <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Không tải được chi tiết dự toán: {error.message}
          </Card>
        ) : (
          <Card className="flex h-full flex-col overflow-hidden border-slate-200 bg-white shadow-sm">
            <div className="flex shrink-0 flex-col gap-3 border-b border-slate-100 bg-slate-50/50 p-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h3 className="text-sm font-medium text-slate-800">Bảng khối lượng & đơn giá dự toán</h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  Tổng giá trị: {formatCurrency(stats?.totalAmount || estimate?.total_amount || 0)}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {canSendReview && (
                  <button
                    onClick={handleSendReview}
                    disabled={isUpdatingStatus}
                    className="flex h-10 items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 text-xs font-medium text-amber-700 shadow-sm transition-colors hover:bg-amber-100 sm:text-sm"
                  >
                    <i className="ri-send-plane-line text-lg" />
                    Gửi duyệt
                  </button>
                )}
                {estimate?.status_code === 'reviewing' && (
                  <button
                    onClick={handleApprove}
                    disabled={isUpdatingStatus}
                    className="flex h-10 items-center gap-2 rounded-md bg-emerald-700 px-4 text-xs font-medium text-white shadow-sm transition-colors hover:bg-emerald-800 sm:text-sm"
                  >
                    <i className="ri-check-line text-lg" />
                    Duyệt
                  </button>
                )}
                {canLock && (
                  <button
                    onClick={handleLock}
                    disabled={isCreatingSnapshot || isUpdatingStatus}
                    className="flex h-10 items-center gap-2 rounded-md bg-slate-800 px-4 text-xs font-medium text-white shadow-sm transition-colors hover:bg-slate-900 disabled:opacity-60 sm:text-sm"
                  >
                    <i className="ri-lock-line text-lg" />
                    Khóa version
                  </button>
                )}
                <button onClick={handleExportCsv} className="flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-4 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 sm:text-sm">
                  <i className="ri-file-excel-2-line text-lg text-emerald-600" />
                  Xuất Excel
                </button>
                {canEdit && (
                  <button onClick={() => setShowImportModal(true)} className="flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-4 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 sm:text-sm">
                    <i className="ri-upload-2-line text-lg text-blue-600" />
                    Nhập CSV
                  </button>
                )}
                {canEdit && (
                  <>
                    <button
                      onClick={() => setShowAddItem(true)}
                      className="flex h-10 items-center gap-2 rounded-md bg-emerald-600 px-4 text-xs font-medium text-white shadow-sm transition-colors hover:bg-emerald-700 sm:text-sm"
                    >
                      <i className="ri-add-line text-lg" />
                      Thêm hạng mục
                    </button>
                    <button
                      onClick={() => navigate('/estimates/norms')}
                      className="flex h-10 items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-4 text-xs font-medium text-emerald-700 shadow-sm transition-colors hover:bg-emerald-100 sm:text-sm"
                    >
                      <i className="ri-book-open-line text-lg" />
                      Định mức
                    </button>
                  </>
                )}
                {canConvert && (
                  <button
                    onClick={handleConvertToBoq}
                    disabled={isConverting}
                    className="flex h-10 items-center gap-2 rounded-md bg-blue-700 px-4 text-xs font-medium text-white shadow-sm transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
                  >
                    <i className="ri-arrow-right-circle-line text-lg" />
                    {isConverting ? 'Đang chuyển...' : 'Chuyển BOQ'}
                  </button>
                )}
              </div>
            </div>

            <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-slate-100 bg-white px-3 pt-2">
              {detailTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex h-10 shrink-0 items-center gap-2 border-b-2 px-3 text-xs font-medium transition-colors sm:text-sm ${
                    activeTab === tab.id
                      ? 'border-emerald-600 text-emerald-700'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <i className={tab.icon} />
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-auto">
              {activeTab === 'overview' && (
                <EstimateOverviewPanel estimate={estimate} pricing={pricing} itemCount={items.length} resourceCount={resources.length} versionCount={versions.length} />
              )}
              {activeTab === 'items' && (
                <EstimatesTable
                  items={items}
                  isLoading={isLoadingEstimate || isLoadingItems}
                  canEdit={!!canEdit}
                  onRowClick={setSelectedItemId}
                  onEdit={setEditingItem}
                  onDelete={handleDeleteItem}
                />
              )}
              {activeTab === 'resources' && (
                <EstimateResourcesPanel estimateId={estimateId} resources={resources} isLoading={isLoadingResources} />
              )}
              {activeTab === 'norms' && (
                <EstimateNormsPanel companyId={estimate?.company_id} items={items} resources={resources} onOpenNorms={() => navigate('/estimates/norms')} />
              )}
              {activeTab === 'pricing' && (
                <EstimatePricingPanel
                  items={items}
                  pricing={pricing}
                  overheadPercent={overheadPercent}
                  setOverheadPercent={setOverheadPercent}
                  profitPercent={profitPercent}
                  setProfitPercent={setProfitPercent}
                  taxPercent={taxPercent}
                  setTaxPercent={setTaxPercent}
                />
              )}
              {activeTab === 'zones' && (
                <EstimateZonesPanel items={items} />
              )}
              {activeTab === 'versions' && (
                <EstimateVersionsPanel versions={versions} isLoading={isCreatingSnapshot && versions.length === 0} />
              )}
              {activeTab === 'boq-links' && (
                <EstimateBoqLinksPanel links={links} isLoading={isLoadingLinks} onOpenBoq={(boqId) => navigate(`/construction/boq/${boqId}`)} />
              )}
              {activeTab === 'logs' && (
                <EstimateLogsPanel estimate={estimate} items={items} versions={versions.length} links={links.length} />
              )}
            </div>
          </Card>
        )}
      </div>

      <div className={`fixed right-0 top-0 z-50 h-screen w-full max-w-[420px] transform border-l border-slate-200 bg-white shadow-2xl transition-transform duration-300 ${selectedItem ? 'translate-x-0' : 'translate-x-full'}`}>
        {selectedItem && <EstimateItemDrawer item={selectedItem} canApplyProcess={!!canEdit} onApplyProcess={setProcessItem} onClose={() => setSelectedItemId(null)} />}
      </div>

      {showAddItem && estimate && (
        <EstimateItemFormModal
          companyId={estimate.company_id}
          projectId={estimate.project_id}
          isSaving={isCreating}
          onClose={() => setShowAddItem(false)}
          onSubmit={handleCreateItem}
        />
      )}
      {editingItem && estimate && (
        <EstimateItemFormModal
          item={editingItem}
          companyId={estimate.company_id}
          projectId={estimate.project_id}
          isSaving={isUpdating}
          onClose={() => setEditingItem(null)}
          onSubmit={handleUpdateItem}
        />
      )}
      {showImportModal && estimate && (
        <EstimateImportModal
          companyId={estimate.company_id}
          projectId={estimate.project_id}
          estimateId={estimateId!}
          onClose={() => setShowImportModal(false)}
        />
      )}
      {processItem && estimateId && (
        <ProcessApplyDrawer item={processItem} estimateId={estimateId} onClose={() => setProcessItem(null)} />
      )}
      <ConfirmDialog
        isOpen={!!deleteItemTarget}
        title="Xóa hạng mục dự toán"
        message={`Xóa hạng mục ${deleteItemTarget?.item_code || deleteItemTarget?.item_name || ''}?`}
        confirmText="Xóa"
        isDestructive
        onConfirm={confirmDeleteItem}
        onClose={() => setDeleteItemTarget(null)}
      />
    </div>
  );
}

function EstimateOverviewPanel({
  estimate,
  pricing,
  itemCount,
  resourceCount,
  versionCount,
}: {
  estimate: EstimateHeader | null;
  pricing: ReturnType<typeof useEstimatePricing>;
  itemCount: number;
  resourceCount: number;
  versionCount: number;
}) {
  return (
    <div className="grid gap-4 p-4 lg:grid-cols-3">
      <section className="rounded-md border border-slate-200 bg-white p-4 lg:col-span-2">
        <h4 className="text-sm font-medium text-slate-900">Thông tin hồ sơ</h4>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <InfoBlock label="Mã dự toán" value={estimate?.estimate_code || '-'} />
          <InfoBlock label="Tên dự toán" value={estimate?.estimate_name || '-'} />
          <InfoBlock label="Khách hàng" value={estimate?.customer_name || estimate?.project?.name || 'Chưa gắn'} />
          <InfoBlock label="Loại hồ sơ" value={estimate?.estimate_type || '-'} />
          <InfoBlock label="Trạng thái" value={String(estimate?.status_code || '-')} />
          <InfoBlock label="Phiên bản" value={String(estimate?.version_no || 1)} />
        </div>
      </section>

      <section className="rounded-md border border-slate-200 bg-white p-4">
        <h4 className="text-sm font-medium text-slate-900">Kiểm tra nhanh</h4>
        <div className="mt-4 space-y-3">
          <InfoBlock label="Hạng mục" value={String(itemCount)} />
          <InfoBlock label="Tài nguyên" value={String(resourceCount)} />
          <InfoBlock label="Phiên bản khóa" value={String(versionCount)} />
          <InfoBlock label="Cảnh báo" value={String(pricing.warningCount)} />
        </div>
      </section>

      <section className="rounded-md border border-slate-200 bg-white p-4 lg:col-span-3">
        <h4 className="text-sm font-medium text-slate-900">Cơ cấu chi phí</h4>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <InfoBlock label="Vật tư" value={formatCurrency(pricing.materialCost)} />
          <InfoBlock label="Nhân công" value={formatCurrency(pricing.laborCost)} />
          <InfoBlock label="Thiết bị" value={formatCurrency(pricing.equipmentCost)} />
          <InfoBlock label="Thầu phụ" value={formatCurrency(pricing.subcontractCost)} />
          <InfoBlock label="Tạm tính" value={formatCurrency(pricing.subtotal)} />
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 border-t border-slate-100 pt-4">
          <InfoBlock label="Quản lý chung" value={formatCurrency(pricing.overheadAmount)} />
          <InfoBlock label="Lợi nhuận" value={formatCurrency(pricing.profitAmount)} />
          <InfoBlock label="Thuế VAT" value={formatCurrency(pricing.taxAmount)} />
          <div>
            <p className="mb-1 text-xs text-slate-500">Tổng cộng</p>
            <p className="text-lg font-medium text-emerald-700">{formatCurrency(pricing.totalAmount)}</p>
          </div>
        </div>
      </section>
    </div>
  );
}

function EstimateResourcesPanel({ estimateId, resources, isLoading }: { estimateId: string; resources: EstimateItemResource[]; isLoading: boolean }) {
  const applyRates = useApplyRatesToEstimate();
  const overrideMutation = useOverrideResourcePrice();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newPrice, setNewPrice] = useState<number>(0);
  const [reason, setReason] = useState<string>('');

  if (isLoading) return <EmptyPanel text="Đang tải tài nguyên..." />;
  if (resources.length === 0) return <EmptyPanel text="Chưa có tài nguyên bung từ định mức." />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end px-4 pt-4">
        <button
          onClick={() => applyRates.mutate(estimateId)}
          disabled={applyRates.isPending}
          className="flex items-center gap-2 rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-70"
        >
          {applyRates.isPending ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-money-dollar-circle-line" />}
          Áp đơn giá hệ thống
        </button>
      </div>
      <div className="overflow-auto px-4 pb-4">
        <table className="w-full min-w-[900px] border-collapse text-left border border-slate-200">
          <thead className="bg-slate-100">
          <tr>
            <th className="p-3 text-xs font-medium text-slate-600">Loại</th>
            <th className="p-3 text-xs font-medium text-slate-600">Mã</th>
            <th className="p-3 text-xs font-medium text-slate-600">Tên tài nguyên</th>
            <th className="p-3 text-right text-xs font-medium text-slate-600">Định mức</th>
            <th className="p-3 text-right text-xs font-medium text-slate-600">Khối lượng</th>
            <th className="p-3 text-right text-xs font-medium text-slate-600">Đơn giá</th>
            <th className="p-3 text-right text-xs font-medium text-slate-600">Thành tiền</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {resources.map((resource) => (
            <tr key={resource.id} className="hover:bg-slate-50">
              <td className="p-3 text-xs text-slate-600">{resource.resource_type}</td>
              <td className="p-3 text-xs font-medium text-blue-600">{resource.resource_code || '-'}</td>
              <td className="p-3 text-sm text-slate-800">{resource.resource_name}</td>
              <td className="p-3 text-right text-xs text-slate-600">{resource.norm_quantity.toLocaleString('vi-VN')} {resource.unit}</td>
              <td className="p-3 text-right text-xs text-slate-600">{resource.quantity.toLocaleString('vi-VN')} {resource.unit}</td>
              <td className="p-3 text-right text-xs text-slate-600">
                {editingId === resource.id ? (
                  <div className="flex flex-col gap-1 items-end">
                    <input type="number" value={newPrice} onChange={(e) => setNewPrice(Number(e.target.value))} className="w-24 border rounded px-1 text-xs" />
                    <input type="text" placeholder="Lý do" value={reason} onChange={(e) => setReason(e.target.value)} className="w-24 border rounded px-1 text-xs" />
                    <div className="flex gap-1">
                      <button onClick={() => setEditingId(null)} className="text-xs text-slate-500">Hủy</button>
                      <button
                        onClick={() => {
                          if (!reason.trim()) {
                            toast.error('Vui lòng nhập lý do ghi đè đơn giá.');
                            return;
                          }
                          overrideMutation.mutate({ resourceId: resource.id, estimateId, newPrice, reason }, { onSuccess: () => setEditingId(null) });
                        }}
                        className="text-xs text-emerald-600 font-medium"
                      >
                        Lưu
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-end group">
                    <span className="flex items-center gap-1">
                      {formatCurrency(resource.unit_price)}
                      <button onClick={() => { setEditingId(resource.id); setNewPrice(resource.unit_price); setReason(''); }} className="opacity-0 group-hover:opacity-100 text-blue-600">
                        <i className="ri-edit-line text-xs" />
                      </button>
                    </span>
                    {resource.price_source?.includes('Override') && (
                      <span className="text-xs text-amber-600 truncate max-w-[100px]" title={resource.note}>
                        {resource.note || 'Có điều chỉnh'}
                      </span>
                    )}
                  </div>
                )}
              </td>
              <td className="p-3 text-right text-sm font-medium text-slate-900">{formatCurrency(resource.total_amount)}</td>
            </tr>
          ))}
        </tbody>
        </table>
      </div>
    </div>
  );
}

function EstimateVersionsPanel({ versions, isLoading }: { versions: EstimateVersion[]; isLoading: boolean }) {
  if (isLoading) return <EmptyPanel text="Đang tải phiên bản..." />;
  if (versions.length === 0) return <EmptyPanel text="Chưa có snapshot phiên bản." />;

  return (
    <div className="space-y-3 p-4">
      {versions.map((version) => (
        <div key={version.id} className="rounded-md border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-slate-900">Version {version.version_no}</p>
              <p className="mt-0.5 text-xs text-slate-500">{new Date(version.created_at).toLocaleString('vi-VN')}</p>
            </div>
            <p className="text-sm font-medium text-slate-900">{formatCurrency(version.total_amount)}</p>
          </div>
          {version.note && <p className="mt-2 text-xs text-slate-500">{version.note}</p>}
        </div>
      ))}
    </div>
  );
}

function EstimateBoqLinksPanel({
  links,
  isLoading,
  onOpenBoq,
}: {
  links: EstimateBoqLink[];
  isLoading: boolean;
  onOpenBoq: (boqId: string) => void;
}) {
  if (isLoading) return <EmptyPanel text="Đang tải BOQ liên kết..." />;
  if (links.length === 0) return <EmptyPanel text="Chưa có BOQ liên kết." />;

  return (
    <div className="overflow-auto">
      <table className="w-full min-w-[760px] border-collapse text-left">
        <thead className="bg-slate-100">
          <tr>
            <th className="p-3 text-xs font-medium text-slate-600">BOQ</th>
            <th className="p-3 text-xs font-medium text-slate-600">Hạng mục BOQ</th>
            <th className="p-3 text-xs font-medium text-slate-600">Ngày tạo link</th>
            <th className="p-3 text-right text-xs font-medium text-slate-600">Thao tác</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {links.map((link) => (
            <tr key={link.id} className="hover:bg-slate-50">
              <td className="p-3 text-sm font-medium text-slate-900">{link.boq?.boq_code || link.boq_id || '-'}</td>
              <td className="p-3 text-sm text-slate-700">{link.boq_item?.item_name || link.boq_item_id || '-'}</td>
              <td className="p-3 text-xs text-slate-500">{new Date(link.created_at).toLocaleString('vi-VN')}</td>
              <td className="p-3 text-right">
                {link.boq_id && (
                  <button onClick={() => onOpenBoq(link.boq_id!)} className="rounded-md border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50">
                    Mở BOQ
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EstimateLogsPanel({
  estimate,
  items,
  versions,
  links,
}: {
  estimate: EstimateHeader | null;
  items: EstimateItem[];
  versions: number;
  links: number;
}) {
  const rows = [
    { label: 'Tạo hồ sơ', value: estimate?.created_at ? new Date(estimate.created_at).toLocaleString('vi-VN') : '-' },
    { label: 'Cập nhật gần nhất', value: estimate?.updated_at ? new Date(estimate.updated_at).toLocaleString('vi-VN') : '-' },
    { label: 'Số hạng mục hiện tại', value: String(items.length) },
    { label: 'Số snapshot version', value: String(versions) },
    { label: 'Số link BOQ', value: String(links) },
  ];

  return (
    <div className="space-y-2 p-4">
      {rows.map((row) => (
        <div key={row.label} className="flex items-center justify-between rounded-md border border-slate-200 bg-white p-3">
          <span className="text-sm text-slate-600">{row.label}</span>
          <span className="text-sm font-medium text-slate-900">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

function EmptyPanel({ text }: { text: string }) {
  return <div className="p-8 text-center text-sm text-slate-500">{text}</div>;
}

function EstimateNormsPanel({
  companyId,
  items,
  resources,
  onOpenNorms,
}: {
  companyId?: string;
  items: EstimateItem[];
  resources: EstimateItemResource[];
  onOpenNorms: () => void;
}) {
  const resourceItemIds = new Set(resources.map((resource) => resource.estimate_item_id));
  const missingNormItems = items.filter((item) => !resourceItemIds.has(item.id));
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white p-4">
        <div>
          <p className="text-sm font-medium text-slate-900">Tình trạng áp định mức</p>
          <p className="mt-0.5 text-xs text-slate-500">{items.length - missingNormItems.length}/{items.length} hạng mục đã có tài nguyên định mức.</p>
        </div>
        <button onClick={onOpenNorms} className="rounded-md bg-emerald-700 px-3 py-2 text-sm text-white hover:bg-emerald-800">
          Mở thư viện định mức
        </button>
      </div>
      {missingNormItems.length === 0 ? (
        <EmptyPanel text="Tất cả hạng mục hiện tại đã có tài nguyên định mức hoặc chưa có hạng mục cần kiểm tra." />
      ) : (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-medium text-amber-800">Hạng mục thiếu định mức</p>
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            {missingNormItems.map((item) => (
              <div key={item.id} className="rounded-md border border-amber-100 bg-white px-3 py-2 text-sm text-slate-700">
                 <div className="flex justify-between items-start">
                   <div>
                     <p className="font-medium text-slate-900">{item.item_code} - {item.item_name}</p>
                     <p className="text-xs text-slate-500">Đơn vị: {item.unit} | KL: {item.quantity}</p>
                   </div>
                   <button onClick={() => setSelectedItemId(item.id === selectedItemId ? null : item.id)} className="text-emerald-600 text-xs font-medium hover:underline">
                     {item.id === selectedItemId ? 'Hủy' : 'Áp ĐM'}
                   </button>
                 </div>
                 {selectedItemId === item.id && companyId && (
                   <NormSelector companyId={companyId} item={item} onCancel={() => setSelectedItemId(null)} />
                 )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NormSelector({ companyId, item, onCancel }: { companyId: string, item: EstimateItem, onCancel: () => void }) {
  const { data: norms, isLoading } = useEstimateNorms(companyId, item.category_code);
  const applyMutation = useApplyNormToItem();

  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      {isLoading ? <p className="text-xs text-slate-500">Đang tải thư viện...</p> : (
         <div className="space-y-2">
           <p className="text-xs font-medium text-slate-700">Chọn định mức áp dụng:</p>
           <select
             className="w-full text-xs border border-slate-200 rounded p-1.5 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
             onChange={(e) => {
               if(e.target.value) {
                 applyMutation.mutate({ item, normId: e.target.value }, { onSuccess: onCancel });
               }
             }}
             disabled={applyMutation.isPending}
           >
             <option value="">-- Chọn định mức --</option>
             {norms?.map(n => (
               <option key={n.id} value={n.id}>{n.norm_code} - {n.norm_name}</option>
             ))}
             {norms?.length === 0 && <option disabled>Không có định mức cho nhóm này</option>}
           </select>
           {applyMutation.isPending && <p className="text-xs text-emerald-600 flex items-center gap-1"><i className="ri-loader-4-line animate-spin" /> Đang áp dụng...</p>}
         </div>
      )}
    </div>
  );
}

function EstimatePricingPanel({
  items,
  pricing,
  overheadPercent,
  setOverheadPercent,
  profitPercent,
  setProfitPercent,
  taxPercent,
  setTaxPercent,
}: {
  items: EstimateItem[];
  pricing: ReturnType<typeof useEstimatePricing>;
  overheadPercent: number;
  setOverheadPercent: (v: number) => void;
  profitPercent: number;
  setProfitPercent: (v: number) => void;
  taxPercent: number;
  setTaxPercent: (v: number) => void;
}) {
  const zeroPriceItems = items.filter((item) => item.unit_price <= 0);

  return (
    <div className="space-y-4 p-4">
      <div className="grid gap-3 md:grid-cols-5">
        <section className="rounded-md border border-slate-200 bg-white p-3">
          <p className="text-xs text-slate-500">Vật tư</p>
          <p className="mt-1 text-sm font-medium text-slate-900">{formatCurrency(pricing.materialCost)}</p>
        </section>
        <section className="rounded-md border border-slate-200 bg-white p-3">
          <p className="text-xs text-slate-500">Nhân công</p>
          <p className="mt-1 text-sm font-medium text-slate-900">{formatCurrency(pricing.laborCost)}</p>
        </section>
        <section className="rounded-md border border-slate-200 bg-white p-3">
          <p className="text-xs text-slate-500">Thiết bị</p>
          <p className="mt-1 text-sm font-medium text-slate-900">{formatCurrency(pricing.equipmentCost)}</p>
        </section>
        <section className="rounded-md border border-slate-200 bg-white p-3">
          <p className="text-xs text-slate-500">Thầu phụ</p>
          <p className="mt-1 text-sm font-medium text-slate-900">{formatCurrency(pricing.subcontractCost)}</p>
        </section>
        <section className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-xs font-medium text-emerald-800">Chi phí trực tiếp (Tạm tính)</p>
          <p className="mt-1 text-sm font-medium text-emerald-900">{formatCurrency(pricing.subtotal)}</p>
        </section>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <section className="rounded-md border border-slate-200 bg-white p-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Quản lý chung (%)</span>
            <input type="number" min="0" max="100" value={overheadPercent} onChange={(e) => setOverheadPercent(Number(e.target.value))} className="h-8 w-full rounded border-slate-200 px-2 text-sm" />
          </label>
          <p className="mt-2 text-sm font-medium text-slate-900">{formatCurrency(pricing.overheadAmount)}</p>
        </section>
        <section className="rounded-md border border-slate-200 bg-white p-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Lợi nhuận định mức (%)</span>
            <input type="number" min="0" max="100" value={profitPercent} onChange={(e) => setProfitPercent(Number(e.target.value))} className="h-8 w-full rounded border-slate-200 px-2 text-sm" />
          </label>
          <p className="mt-2 text-sm font-medium text-slate-900">{formatCurrency(pricing.profitAmount)}</p>
        </section>
        <section className="rounded-md border border-slate-200 bg-white p-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Thuế VAT (%)</span>
            <input type="number" min="0" max="100" value={taxPercent} onChange={(e) => setTaxPercent(Number(e.target.value))} className="h-8 w-full rounded border-slate-200 px-2 text-sm" />
          </label>
          <p className="mt-2 text-sm font-medium text-slate-900">{formatCurrency(pricing.taxAmount)}</p>
        </section>
        <section className="rounded-md border border-blue-200 bg-blue-50 p-3 flex flex-col justify-end">
          <p className="text-xs font-medium text-blue-800">Tổng giá trị dự toán</p>
          <p className="mt-1 text-lg font-medium text-blue-900">{formatCurrency(pricing.totalAmount)}</p>
        </section>
      </div>
      {zeroPriceItems.length === 0 ? (
        <EmptyPanel text="Không có hạng mục thiếu đơn giá." />
      ) : (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-4">
          <p className="text-sm font-medium text-rose-800">Hạng mục thiếu đơn giá</p>
          <div className="mt-3 space-y-2">
            {zeroPriceItems.map((item) => (
              <div key={item.id} className="rounded-md border border-rose-100 bg-white px-3 py-2 text-sm text-slate-700">
                <span className="font-medium">{item.item_code}</span> - {item.item_name}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EstimateZonesPanel({ items }: { items: EstimateItem[] }) {
  const groups = items.reduce<Record<string, { name: string; count: number; total: number }>>((acc, item) => {
    const key = item.zone_id || 'none';
    const name = item.zone?.name || 'Chưa gắn khu vực';
    acc[key] = acc[key] || { name, count: 0, total: 0 };
    acc[key].count += 1;
    acc[key].total += item.total_amount;
    return acc;
  }, {});

  const rows = Object.values(groups);
  if (rows.length === 0) return <EmptyPanel text="Chưa có dữ liệu khu vực." />;

  return (
    <div className="overflow-auto">
      <table className="w-full min-w-[640px] border-collapse text-left">
        <thead className="bg-slate-100">
          <tr>
            <th className="p-3 text-xs font-medium text-slate-600">Khu vực</th>
            <th className="p-3 text-right text-xs font-medium text-slate-600">Số hạng mục</th>
            <th className="p-3 text-right text-xs font-medium text-slate-600">Giá trị</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => (
            <tr key={row.name} className="hover:bg-slate-50">
              <td className="p-3 text-sm font-medium text-slate-900">{row.name}</td>
              <td className="p-3 text-right text-sm text-slate-700">{row.count}</td>
              <td className="p-3 text-right text-sm font-medium text-slate-900">{formatCurrency(row.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EstimateItemFormModal({
  item,
  companyId,
  projectId,
  isSaving,
  onClose,
  onSubmit,
}: {
  item?: EstimateItem | null;
  companyId: string;
  projectId?: string | null;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (input: Omit<EstimateItemInput, 'company_id' | 'project_id' | 'estimate_id'>) => Promise<void>;
}) {
  const { zones, isLoading: isLoadingZones } = useProjectZones(companyId, projectId ? { projectId } : {});
  const { phases, isLoading: isLoadingPhases } = useProjectPhases(companyId, projectId ? { projectId } : {});

  const [form, setForm] = useState({
    category_code: item?.category_code || 'trees',
    item_code: item?.item_code || '',
    item_name: item?.item_name || '',
    unit: item?.unit || 'cây',
    quantity: item?.quantity || 1,
    unit_price: item?.unit_price || 0,
    specification: item?.specification || '',
    species: item?.species || '',
    zone_id: item?.zone_id || '',
    phase_id: item?.phase_id || '',
  });

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    await onSubmit({
      category_code: form.category_code,
      item_code: form.item_code.trim(),
      item_name: form.item_name.trim(),
      unit: form.unit.trim(),
      quantity: Number(form.quantity || 0),
      unit_price: Number(form.unit_price || 0),
      material_cost: Number(form.quantity || 0) * Number(form.unit_price || 0),
      labor_cost: 0,
      equipment_cost: 0,
      subcontract_cost: 0,
      specification: form.specification.trim() || null,
      species: form.species.trim() || null,
      zone_id: form.zone_id || null,
      phase_id: form.phase_id || null,
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/30 p-4">
      <form onSubmit={handleSubmit} className="w-full max-w-2xl rounded-lg bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 p-4">
          <h3 className="font-medium text-slate-900">{item ? 'Sửa hạng mục dự toán' : 'Thêm hạng mục dự toán'}</h3>
          <button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="grid gap-4 p-4 md:grid-cols-2">
          <label className="space-y-1">
            <span className="text-xs font-medium text-slate-600">Nhóm</span>
            <select value={form.category_code} onChange={(event) => setForm((current) => ({ ...current, category_code: event.target.value }))} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm">
              <option value="trees">Cây xanh</option>
              <option value="shrubs">Cây bụi</option>
              <option value="grass">Cỏ / phủ nền</option>
              <option value="irrigation">Tưới</option>
              <option value="lighting">Đèn</option>
              <option value="hardscape">Hardscape</option>
              <option value="folder">Thư mục (Nhóm)</option>
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium text-slate-600">Mã hạng mục</span>
            <input required value={form.item_code} onChange={(event) => setForm((current) => ({ ...current, item_code: event.target.value }))} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm" />
          </label>
          <label className="space-y-1 md:col-span-2">
            <span className="text-xs font-medium text-slate-600">Tên hạng mục</span>
            <input required value={form.item_name} onChange={(event) => setForm((current) => ({ ...current, item_name: event.target.value }))} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm" />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium text-slate-600">ĐVT</span>
            <input required value={form.unit} onChange={(event) => setForm((current) => ({ ...current, unit: event.target.value }))} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm" />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium text-slate-600">Loài cây / chủng loại</span>
            <input value={form.species} onChange={(event) => setForm((current) => ({ ...current, species: event.target.value }))} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm" />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium text-slate-600">Khối lượng</span>
            <input type="number" min="0" step="0.0001" required value={form.quantity} onChange={(event) => setForm((current) => ({ ...current, quantity: Number(event.target.value) }))} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm" />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium text-slate-600">Đơn giá</span>
            <input type="number" min="0" step="1000" required value={form.unit_price} onChange={(event) => setForm((current) => ({ ...current, unit_price: Number(event.target.value) }))} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm" />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium text-slate-600">Khu vực</span>
            <select value={form.zone_id} onChange={(event) => setForm((current) => ({ ...current, zone_id: event.target.value }))} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm">
              <option value="">-- Không gắn khu vực --</option>
              {zones?.map((z) => (
                <option key={z.id} value={z.id}>{z.zone_name}</option>
              ))}
            </select>
            {isLoadingZones && <p className="text-xs text-slate-400">Đang tải khu vực...</p>}
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium text-slate-600">Giai đoạn thi công</span>
            <select value={form.phase_id} onChange={(event) => setForm((current) => ({ ...current, phase_id: event.target.value }))} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm">
              <option value="">-- Không gắn giai đoạn --</option>
              {phases?.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            {isLoadingPhases && <p className="text-xs text-slate-400">Đang tải giai đoạn...</p>}
          </label>
          <label className="space-y-1 md:col-span-2">
            <span className="text-xs font-medium text-slate-600">Quy cách</span>
            <input value={form.specification} onChange={(event) => setForm((current) => ({ ...current, specification: event.target.value }))} className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm" />
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 p-4">
          <button type="button" onClick={onClose} className="h-10 rounded-md border border-slate-200 px-4 text-sm text-slate-700">Hủy</button>
          <button type="submit" disabled={isSaving} className="h-10 rounded-md bg-emerald-700 px-4 text-sm text-white disabled:opacity-60">
            {isSaving ? 'Đang lưu...' : 'Lưu hạng mục'}
          </button>
        </div>
      </form>
    </div>
  );
}

function EstimateItemDrawer({ item, canApplyProcess, onApplyProcess, onClose }: { item: EstimateItem; canApplyProcess: boolean; onApplyProcess: (item: EstimateItem) => void; onClose: () => void }) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 p-4">
        <div>
          <h3 className="font-medium text-slate-800">{item.item_code}</h3>
          <p className="mt-0.5 text-xs text-slate-500">{item.item_name}</p>
        </div>
        <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-200">
          <i className="ri-close-line text-lg" />
        </button>
      </div>
      <div className="flex-1 space-y-5 overflow-auto p-4">
        {canApplyProcess && (
          <button
            type="button"
            onClick={() => onApplyProcess(item)}
            className="flex h-10 w-full items-center justify-center gap-2 rounded-md bg-emerald-700 px-4 sm:text-sm text-xs font-medium text-white transition-colors hover:bg-emerald-800"
          >
            <i className="ri-node-tree" />
            Áp quy trình mẫu
          </button>
        )}
        <InfoBlock label="Quy cách" value={item.specification || 'Chưa có'} />
        <InfoBlock label="Loài cây / chủng loại" value={item.species || 'Chưa có'} />
        <InfoBlock label="Khu vực" value={item.zone?.name || 'Chưa gắn khu vực'} />
        <InfoBlock label="Giai đoạn" value={item.phase?.name || 'Chưa gắn giai đoạn'} />
        <div className="grid grid-cols-2 gap-3">
          <InfoBlock label="Khối lượng" value={`${item.quantity.toLocaleString('vi-VN')} ${item.unit}`} />
          <InfoBlock label="Đơn giá" value={formatCurrency(item.unit_price)} />
          <InfoBlock label="Vật tư" value={formatCurrency(item.material_cost)} />
          <InfoBlock label="Nhân công" value={formatCurrency(item.labor_cost)} />
          <InfoBlock label="Thiết bị" value={formatCurrency(item.equipment_cost)} />
          <InfoBlock label="Thành tiền" value={formatCurrency(item.total_amount)} />
        </div>
      </div>
    </div>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="mb-1 text-xs text-slate-500">{label}</p>
      <p className="text-sm font-medium text-slate-800">{value}</p>
    </div>
  );
}
